import { copyDashboardConfig, resolveDashboardConfig } from "@/lib/dashboard/composition";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useRestClient } from "@/lib/restClient";
import type { DashboardRecord } from "@/lib/dashboard/types";
import { dashboardQueryKey } from "./useDashboard";

export interface CreateDashboardInput {
  kind?: DashboardRecord["kind"];
  name: string;
  catalog_tenant_ids?: number[];
  slug: string;
  description?: string;
  status?: string;
  order: number;
  config?: DashboardRecord["config"];
  state?: DashboardRecord["state"];
}

export function buildDashboardCopyInput(dashboard: DashboardRecord): CreateDashboardInput {
  if (dashboard.kind === "template") throw new Error("Use the authorized template copy operation");
  const source = dashboard.draft ?? dashboard;
  const name = `${source.name} (copy)`;
  return {
    name,
    slug: `dashboard-${crypto.randomUUID()}`,
    description: source.description ?? "",
    config: { ...copyDashboardConfig(resolveDashboardConfig(source.config)), title: name },
    order: 0,
    state: "draft",
  };
}

export function buildCreateDashboardBody(body: CreateDashboardInput) {
  return { ...body, config: body.config ?? {}, sources: [] };
}

export function buildTemplateCaptureInput(
  name: string,
  description: string,
  config: DashboardRecord["config"],
): CreateDashboardInput {
  return {
    kind: "template",
    name,
    description,
    slug: `template-${crypto.randomUUID()}`,
    order: 0,
    state: "saved",
    config,
  };
}

export function useCreateDashboard() {
  const apiFetch = useRestClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateDashboardInput) =>
      apiFetch<DashboardRecord>("/api/dashboards", {
        method: "POST",
        body: JSON.stringify(buildCreateDashboardBody(body)),
      }),
    onSuccess: (dashboard) => {
      queryClient.setQueryData(dashboardQueryKey(dashboard.id, dashboard.kind), dashboard);
      void queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
}
